/* eslint-disable import/no-default-export, import/order, max-lines, max-params, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { api } from "jiffy-api";
import * as ActionCable from "@rails/actioncable";

import "common/jiffy/cable";
import { findIndexById } from "common/utils/helpers/find-index-by-id";
import {
  updateHTML,
  setPDPDatesLoading
} from "components/jiffy/product-details-delivery-date-simple/init-dates";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { submitProcolorRoutableTaggedForArtwork } from "common/utils/mixpanel/dtf-procolor-event";
import { trackRudderstackImageProcessingCompleted } from "common/utils/rudderstack/tracker.react";
import { BUSINESS_LINE } from "common/utils/app-registry/contants";
import { DTF_PROGRESS_MAPPING } from "common/utils/progress-bar/constants";
import { DEFAULT_STICKER_VALUES } from "common/utils/stickers/constants";
import { notifyError } from "common/utils/app-monitoring/notify";

import { WS_DISCONNECT_POLLING_DELAY } from "./common/constants";
import ErrorTypes from "./error-types";
// eslint-disable-next-line import/no-cycle
import { SPANGLE_MODES, spanglesStore } from "./spangles-module";
import { useStickersStore } from "./stickers-module";
import type { SpangleVariation } from "./spangles-module.atoms";
import { recalculateCustomSizes, getRandomId } from "./utils";
import { jotaiStore, upsellVerdictAtom } from "./store.atoms";
import type { UpsellVerdict } from "./store.atoms";

// MN-4814: AppliedTransformation lives in `./dtf-channel.types` so consumers
// (PrintPerfectChecklist + its test) can import it without pulling this whole
// file into Stryker's mutation target set — see `dtf-channel.types.ts`.
import type { AppliedTransformation, AppliedTransformationKey } from "./dtf-channel.types";

export type { AppliedTransformation, AppliedTransformationKey };

declare global {
  interface Window {
    App?: {
      cable?: ActionCable.Consumer;
    };
  }
}

type CableSubscription = {
  perform: (action: string, data?: Record<string, unknown>) => void;
};

/** Metadata sub-object returned by the WebSocket for artwork images. */
type WsArtworkMetadata = {
  dpi?: number;
  uploaded_on?: string;
  width_inch?: number;
  height_inch?: number;
  aspect_ratio?: number;
  white_pixels_ratio?: number;
  whitePixelsRatio?: number;
  width?: number;
  height?: number;
  errors?: unknown[];
  [key: string]: unknown;
};

/** Artwork data object returned inside DtfChannelResult (from WebSocket). */
type WsArtworkData = {
  id?: number;
  url?: string;
  name?: string;
  metadata?: WsArtworkMetadata;
  original_metadata?: WsArtworkMetadata;
  enhanced_metadata?: WsArtworkMetadata;
  original_image_url?: string;
  with_bg_url?: string;
  no_bg_url?: string;
  no_bg_no_trim_url?: string;
  original_dpi?: number;
  original_mime?: string;
  with_bg_imgix_url?: string;
  no_bg_imgix_url?: string;
  original_image_imgix_url?: string;
  filename?: string;
  filetype?: string;
  filesize?: number;
  applied_transformations?: AppliedTransformation[];
  [key: string]: unknown;
};

type DtfChannelResult = {
  success?: boolean;
  artwork_id?: number;
  artwork?: WsArtworkData;
  data?: Record<string, unknown>;
  process_data?: WsArtworkData;
  response?: Record<string, unknown>;
  algorithm?: string;
  duration?: number;
  original_artwork_url?: string;
  pages_info?: Record<string, unknown>;
  positions_json?: string;
  design_url?: string;
  spangle_csv?: string;
  original_image_url?: string;
  algorithms_size?: number;
  error?: string;
  result?: Record<string, unknown>;
  file_type?: string;
  upsell_verdict?: UpsellVerdict;
  // PIP-3054: surfaced by Dtf::Actioncable::ProcessArtwork on the success broadcast.
  procolor_routable?: boolean;
  procolor_routing_mode?: string | null;
  procolor_routing_details?: {
    delta_e_value?: number;
    threshold?: number;
    profile_sha256?: string;
    percentile_label?: string;
  } | null;
};

type DtfChannelMessage = {
  action: string;
  result: DtfChannelResult;
  data?: Record<string, unknown>;
  worker_enhancement?: unknown;
};

/** Shape of an artwork object stored in the DTF store's artworks array. */
type StoreArtwork = {
  id: number | string;
  originalIndex?: string;
  processTimeStamp?: number;
  imageProcessingCompletedEventSent?: boolean;
  enhancementTier?: string;
  isSizesEdited?: boolean;
  sizesAndQuantities?: Array<{
    preset: string;
    width: number;
    height: number;
    [key: string]: unknown;
  }>;
  spangled?: boolean;
  isLoading?: boolean;
  enhancementCompleted?: boolean;
  enhancedDpi?: number;
  appliedTransformations?: AppliedTransformation[];
  lastWebSocketEvent?: string | null;
  lastWebSocketEventData?: Record<string, unknown>;
  shape?: string;
  paddingSize?: string;
  metadata?: { whitePixelsRatio?: number; [key: string]: unknown };
  bgColor?: string;
  [key: string]: unknown;
};

type DtfStoreApi = Record<string, unknown> & {
  artworks: StoreArtwork[];
  businessLine: string;
  dtfChannelUuid: string;
  isSpangle: boolean;
  isSticker: boolean;
  isStickerSample: boolean;
  isStandardArtwork: boolean;
  isGoldFoil: boolean;
  showMultiSize: boolean;
  preCut: string;
  sticker: unknown;
  initialValues: { quantity: number; width: number; height: number; [key: string]: unknown };
  experiments: Record<string, unknown>;
  actionCableConnected: boolean;
  actionCableOptions: {
    artworkIds: string;
    artworkWidth: string;
    artworkHeight: string;
    processArtwork: string;
    urlPreCut: string;
    fileContentType: string;
    artworkPaddingSize: string;
    artworkBgColor: string;
    artworkColor: string;
    artworkShape: string;
  };
  validateArtworkStartTime: Record<string | number, number>;
  updateArtworkValues: (params: Record<string, unknown>) => void;
  updateArtworkValuesById: (params: Record<string, unknown>) => void;
  setActionCableConnected: (connected: boolean) => void;
  setIsRetrying: (retrying: boolean) => void;
  setValidateArtworkStartTime: (artworkId: number | string, time: number) => void;
  clearValidateArtworkStartEventInterval: (artworkId: number | string) => void;
  setValidateArtworkStartEventIntervalID: (
    artworkId: number | string,
    intervalId: ReturnType<typeof setInterval>
  ) => void;
  getArtworksPricing: () => void;
  handleWebSocketEnhancementComplete: (artworkId: number | string) => void;
  setGangsheetValidationError: (params: Record<string, unknown>) => void;
  setArtworkPricing: (params: Record<string, unknown>) => void;
  setLoadedArtwork: (params: Record<string, unknown>) => void;
  setPresetMockupColor: (params: Record<string, unknown>) => void;
  handleProcessError: (params: Record<string, unknown>) => void;
  preloadArtworks: (params: Record<string, unknown>) => void;
  cleanupPolling: () => void;
  schedulePollingFallback: (artworkId: number | string, options?: Record<string, unknown>) => void;
  // MN-4782
  setEnhancementStuck: (artworkId: number | string) => void;
  updatePrintReadyInfoState: (artworkId: number | string) => void;
  convertImageToHexColor: (params: Record<string, unknown>) => void;
  generateGoldFoilImages: (params: Record<string, unknown>) => void;
  toggleLoading: (params: unknown[]) => void;
};

/**
 * Builds the single seed entry written to the store when enhancement completes
 * and the customer has not yet picked a placement (MN-5668).
 * Centralised here so both WS handlers (extract_bg_metadata and fetch_delivery_date)
 * share the exact same shape, isAutoSeed flag, and id generation.
 */
const seedAutoCustomSize = (store: DtfStoreApi): Record<string, unknown> => ({
  preset: "custom",
  width: store.initialValues.width,
  height: store.initialValues.height,
  quantity: store.initialValues.quantity,
  isAutoSeed: true,
  id: getRandomId()
});

const BLACK_STICKER_BACKGROUND_THRESHOLD = 50;
const IMAGE_UPLOAD_ENHANCEMENTS_STUCK_THRESHOLD = 60;

const progressMapping = DTF_PROGRESS_MAPPING as Record<
  string,
  { label: string[]; percentage: number[] }
>;

const isTrackedAction = (action: string) => progressMapping[action] !== undefined;

const setProgress = (data: DtfChannelMessage, store: DtfStoreApi) => {
  const mapping = progressMapping[data.action];
  if (!mapping) return;
  const progressIndex = store.isSpangle ? 1 : 0;
  const { label, percentage } = mapping;
  const progressLabel = label[progressIndex] || label[0];
  const progressPercentage = percentage[progressIndex] || percentage[0];

  store.updateArtworkValuesById({
    id: data.result?.artwork_id || data.data?.["artwork_id"],
    progress: progressPercentage,
    progressMessage: progressLabel
  });
};

const setLastEvent = (data: DtfChannelMessage, store: DtfStoreApi) => {
  // MN-5356: Error broadcasts carry no process_data / top-level data, so
  // persisting them as replay state would produce a payload with
  // { result: undefined, data: undefined } that strips additional_metadata.store
  // on the next continue_dtf_artwork_process replay. Skip replay-state writes
  // for any event where success is not explicitly true — this covers
  // success: false (error broadcasts) and any future shape where success is
  // omitted entirely.
  if (data.result?.success !== true) return;

  store.updateArtworkValuesById({
    id: data.result?.artwork_id || data.data?.["artwork_id"],
    lastWebSocketEvent: data.action,
    lastWebSocketEventData: {
      result: data.result?.process_data || data.data,
      data: data.result?.data || data.data
    }
  });
};

const handleValidateArtworkProgress = (artworkId: number | string, store: DtfStoreApi) => {
  store.setValidateArtworkStartTime(artworkId, Date.now());
  const timerInterval = setInterval(() => {
    const timeElapsed = Math.floor(
      (Date.now() - (store.validateArtworkStartTime[artworkId] ?? 0)) / 1000
    );
    if (timeElapsed > IMAGE_UPLOAD_ENHANCEMENTS_STUCK_THRESHOLD) {
      store.clearValidateArtworkStartEventInterval(artworkId);

      submitDtfEvent("image_upload_enhancements_stuck", {
        dtf_channel_uuid: store.dtfChannelUuid,
        artwork_id: artworkId
      });
      // MN-4782: surface the stuck state to the user (inline amber ArtworkNotification
      // with Retry / Upload-different actions). Firing telemetry alone — as this
      // branch used to — left the user on a forever-spinner.
      store.setEnhancementStuck(artworkId);
    }
  }, 1000);

  store.setValidateArtworkStartEventIntervalID(artworkId, timerInterval);
};

const getArtwork = (
  artworkData: DtfChannelMessage,
  store: DtfStoreApi
): StoreArtwork | undefined => {
  const { result } = artworkData;

  return store.artworks.find(artwork => {
    const artworkId =
      typeof artwork.id === "string" ? (artwork.id.match(/^\d+/) || [])[0] : artwork.id;
    return (
      Number(artworkId) === Number(result?.artwork_id) ||
      (artwork.originalIndex != null &&
        artwork.originalIndex.toString() === String(result?.data?.["file_original_index"]))
    );
  });
};

// eslint-disable-next-line max-lines-per-function,max-params
const markEnhancementCompleted = async (
  store: DtfStoreApi,
  currentArtwork: StoreArtwork,
  artworkData: WsArtworkData,
  result: DtfChannelResult,
  data: DtfChannelMessage
) => {
  if (!currentArtwork || !artworkData) return;

  const { imageProcessingCompletedEventSent } = currentArtwork;

  if (store.isStandardArtwork) {
    const { showMultiSize } = store;
    // Use currentArtwork directly - it was already found by getArtwork() which matches
    // by originalIndex when id is not yet set (race condition with HTTP response)
    const { metadata } = artworkData;
    let sizesAndQuantities: Record<string, unknown>[] = [];

    if (showMultiSize) {
      // MN-4778: do not overwrite a customer-picked placement when enhancement completes.
      // MN-5668: when the store is still empty (fresh upload, customer hasn't interacted),
      // seed a single isAutoSeed custom-size entry so isArtworkValid() unblocks Add to Cart.
      // handlePresetSelect strips isAutoSeed entries when the customer picks a named preset,
      // and mapSizeAndQuantity strips the key before the cart payload reaches the backend.
      if (currentArtwork.isSizesEdited) {
        sizesAndQuantities = recalculateCustomSizes(
          currentArtwork.sizesAndQuantities as Array<{
            preset: string;
            width: number;
            height: number;
            [key: string]: unknown;
          }>,
          metadata?.aspect_ratio as number
        );
      } else {
        const existing = (currentArtwork.sizesAndQuantities as Record<string, unknown>[]) || [];
        sizesAndQuantities = existing.length > 0 ? existing : [seedAutoCustomSize(store)];
      }
    }

    const artworkUpdateData: Record<string, unknown> = {
      id: currentArtwork.id,
      originalImageUrl: artworkData.original_image_url,
      enhancedWithBgUrl: artworkData.with_bg_url,
      enhancedNoBgUrl: artworkData.no_bg_url,
      enhancedDpi: artworkData.metadata?.dpi,
      enhancedOriginalDpi: artworkData.original_dpi,
      originalDpi: 300,
      sliderPreviewUrl: artworkData.no_bg_no_trim_url,
      originalMime: artworkData.original_mime,
      enhancementCompleted: true,
      appliedTransformations: artworkData.applied_transformations,
      sizesAndQuantities
    };

    if (artworkData.with_bg_imgix_url) {
      artworkUpdateData["enhancedWithBgUrl"] = artworkData.with_bg_imgix_url;
    }
    if (artworkData.no_bg_imgix_url) {
      artworkUpdateData["enhancedNoBgUrl"] = artworkData.no_bg_imgix_url;
    }
    artworkUpdateData["sliderPreviewUrl"] =
      artworkData.no_bg_no_trim_url || artworkData.no_bg_imgix_url;
    if (artworkData.original_image_imgix_url) {
      artworkUpdateData["originalImageUrl"] = artworkData.original_image_imgix_url;
    }

    store.updateArtworkValuesById(artworkUpdateData);

    if (store.isGoldFoil) {
      store.generateGoldFoilImages({ artworkId: currentArtwork.id });
    }

    submitDtfEvent("dtf_image_print_perfect_toggle", {
      business_line: store.businessLine,
      artwork_id: result.artwork_id,
      enhance_image: true,
      selected_by: "jiffy",
      metadata: artworkData.metadata
    });
  }

  if (store.artworks.length >= 1 && !store.artworks.some(artwork => artwork.isLoading)) {
    store.getArtworksPricing();
  }

  if (!imageProcessingCompletedEventSent) {
    submitDtfEvent("image_processing_completed", {
      payload: {
        process_data: artworkData,
        stream_name: `dtf_channel_${store.dtfChannelUuid}`,
        action_type: data.action,
        worker_enhancement: data.worker_enhancement
      },
      business_line: store.businessLine,
      artwork_id: result.artwork_id,
      trigger_event: data.action
    });

    // PIP-3054: emit procolor routable event from the WS hot path. Procolor fields
    // ride on `result` (top-level WS payload) — populated by Dtf::Actioncable::ProcessArtwork.
    submitProcolorRoutableTaggedForArtwork(
      { ...result, id: result.artwork_id },
      { multiArtwork: store.artworks.length > 1 }
    );

    const enhancedImageUrl =
      artworkData.no_bg_imgix_url ||
      artworkData.with_bg_imgix_url ||
      artworkData.no_bg_url ||
      artworkData.with_bg_url;
    const originalImageUrl = artworkData.original_image_imgix_url || artworkData.original_image_url;

    trackRudderstackImageProcessingCompleted({
      ...(result.artwork_id != null && { artwork_id: String(result.artwork_id) }),
      dpi: artworkData.metadata?.dpi,
      location: artworkData.metadata?.uploaded_on,
      file_name: artworkData.filename,
      file_type: artworkData.filetype,
      file_size: artworkData.filesize,
      business_line: store.businessLine,
      artworks_count: store.artworks.length,
      pre_cut_transfers: store.preCut,
      ...(enhancedImageUrl && { enhanced_image_url: enhancedImageUrl }),
      ...(originalImageUrl && { original_image_url: originalImageUrl })
    });

    if (result.artwork_id != null) {
      // acknowledgeArtwork needs the backend-assigned id — skip when unknown
      api.dtf
        .acknowledgeArtwork({ id: result.artwork_id }, undefined, { silent: true })
        .catch(() => {
          // Expected: 422 while enhancement is still processing (HB-1851)
        });
    }

    // Always mark local completion to prevent re-emitting image_processing_completed
    // on replayed messages. Prefer the local currentArtwork.id so this still works
    // when the backend identifier arrives via file_original_index fallback.
    const completionId = result.artwork_id ?? currentArtwork.id;
    if (completionId != null) {
      store.updateArtworkValuesById({
        id: completionId,
        imageProcessingCompletedEventSent: true
      });
      store.updatePrintReadyInfoState(completionId);
    }
  }
};

/* eslint-disable max-lines, max-lines-per-function */
export default (store: DtfStoreApi) => {
  const spangleStore = spanglesStore;
  const stickerStore = useStickersStore();
  const params = { uuid: store.dtfChannelUuid, channel: "DtfChannel" };
  const { App } = window;

  /* eslint-disable-next-line no-undef */
  const cable = App!.cable!.subscriptions.create(params, {
    async received(
      this: CableSubscription,
      /** @type {import('jiffy-api').models.cable.DtfChannel.Actions} */ data: DtfChannelMessage
    ) {
      try {
        if (!store.actionCableConnected) {
          store.setActionCableConnected(true);
        }

        const { result } = data;
        let currentArtwork = getArtwork(data, store);

        // For spangle events, artwork_id might not be in the result
        // but there's always only one artwork in the store for spangle products
        if (!currentArtwork && store.isSpangle && data.action === "spangle") {
          currentArtwork = store.artworks?.[0];
        }

        // Check timestamp mismatch, but only if both timestamps exist
        const timeStampMismatch =
          !!result?.data?.["process_time_stamp"] &&
          !!currentArtwork?.processTimeStamp &&
          String(currentArtwork.processTimeStamp) !== String(result.data["process_time_stamp"]);

        if (timeStampMismatch) return;

        if (isTrackedAction(data.action)) {
          store.setIsRetrying(false);
          setProgress(data, store);
          setLastEvent(data, store);
        }

        if (store.isSpangle && store.artworks?.[0]) {
          store.artworks[0].spangled = true;
        }

        if (data.action === "process_dtf_artwork_started") {
          // Reset the upsell verdict so a prior `show_upsell` decision from
          // an earlier upload doesn't carry over into the 2-5s tagging window
          // for the new artwork. Without this the prominent teaser stays
          // visible while GPT classifies the replacement.
          jotaiStore.set(upsellVerdictAtom, "unknown");
        }

        const eventName =
          data.action === "process_dtf_artwork_started"
            ? "image_upload_enhancements_started"
            : `image_upload_${data.action}_triggered`;
        submitDtfEvent(eventName, {
          payload: {
            process_data: result,
            stream_name: `dtf_channel_${store.dtfChannelUuid}`,
            action_type: data.action,
            worker_enhancement: data.worker_enhancement
          },
          business_line: store.businessLine,
          artwork_id: result.artwork_id,
          file_type: result?.file_type
        });

        if (data.action === "spangle") {
          let spangleData: SpangleVariation;

          if (store.isSpangle && spangleStore.spangleVariations.length === 0) {
            spangleStore.prepareVariations({ isRetry: false });
          }

          if (result.success) {
            submitDtfEvent("spangle_api_call_completed", {
              algorithm: result.algorithm,
              duration: (result.duration ?? 0) / 1000,
              artwork_url: result.original_artwork_url,
              pages_info: result.pages_info,
              business_line: BUSINESS_LINE.SPANGLE
            });

            spangleData = {
              algorithm: result.algorithm ?? "",
              imageUrl: result.design_url,
              spangleCsv: result.spangle_csv,
              spanglePagesInfo: result.pages_info,
              positionsJson: result.positions_json,
              originalImageUrl: result.original_image_url,
              error: false,
              isLoading: false
            };

            spangleStore.setOriginalArtworkUrl(result.original_image_url ?? "");
          } else {
            spangleData = { error: true, algorithm: result.algorithm ?? "", isLoading: false };
            notifyError("DTF spangle variations error", {
              params: {
                error_message: result.error,
                algorithm: result.algorithm,
                pages_info: result.pages_info
              }
            });
          }

          // There are times that reset in `removeArtwork` doesn't succeed
          // Adding this reset ensures that completedSpangleData is empty before
          // we proceed.
          if (spangleStore.completedSpangleData.length === result.algorithms_size) {
            spangleStore.resetCompletedSpangleData();
          }

          spangleStore.addCompletedSpangleData(spangleData);

          if (spangleStore.completedSpangleData.length === result.algorithms_size) {
            spangleStore.showSpangleResult();
            spangleStore.setSpangleMode(SPANGLE_MODES.sizeEdited);
          }

          return;
        }

        if (data.action === "spangle_update") {
          store.updateArtworkValues({
            index: 0,
            spangledImageUrl: result.design_url
          });

          return;
        }

        if (data.action === "upload_artwork") {
          if (!data.data?.["backend_triggered_enhancement"]) {
            this.perform("process_dtf_artwork", { ...data.data, experiments: store.experiments });
          }
          return;
        }

        if (data.action === "validate_artwork") {
          if (result.success) {
            if (currentArtwork?.id != null) {
              handleValidateArtworkProgress(currentArtwork.id, store);
            }
            return;
          }

          if (currentArtwork?.id != null) {
            store.clearValidateArtworkStartEventInterval(currentArtwork.id);
            store.handleWebSocketEnhancementComplete(currentArtwork.id);
          }

          // Prefer the backend id, fall back to local currentArtwork.id
          const validatePrintReadyId = result.artwork_id ?? currentArtwork?.id;
          if (validatePrintReadyId != null) {
            store.updatePrintReadyInfoState(validatePrintReadyId);
          }

          const { imageProcessingCompletedEventSent } = currentArtwork || {};

          if (!imageProcessingCompletedEventSent) {
            submitDtfEvent("image_processing_completed", {
              payload: {
                process_data: result,
                stream_name: `dtf_channel_${store.dtfChannelUuid}`,
                action_type: data.action,
                worker_enhancement: data.worker_enhancement
              },
              business_line: store.businessLine,
              artwork_id: result.artwork_id,
              trigger_event: data.action
            });

            trackRudderstackImageProcessingCompleted({
              ...(result.artwork_id != null && { artwork_id: String(result.artwork_id) }),
              dpi: result.artwork?.metadata?.dpi,
              location: result.artwork?.metadata?.uploaded_on,
              file_name: result.artwork?.filename,
              file_type: result.artwork?.filetype,
              file_size: result.artwork?.filesize,
              artworks_count: store.artworks.length,
              business_line: store.businessLine,
              pre_cut_transfers: store.preCut,
              enhanced_image_url:
                result.artwork?.no_bg_imgix_url || result.artwork?.with_bg_imgix_url,
              original_image_url: result.artwork?.original_image_imgix_url
            });

            store.updateArtworkValuesById({
              id: result.artwork_id,
              imageProcessingCompletedEventSent: true
            });
          }
        }

        if (data.action === "process_artwork") {
          if (currentArtwork?.id != null) {
            store.clearValidateArtworkStartEventInterval(currentArtwork.id);
          }
          if (result.success) {
            return;
          }

          if (currentArtwork?.id != null) {
            store.handleWebSocketEnhancementComplete(currentArtwork.id);
          }

          store.getArtworksPricing();
          // Gate on the locally-resolved currentArtwork (getArtwork() already
          // supports the file_original_index identity fallback), not just the
          // backend artwork_id. Otherwise findIndexById's zero-on-miss silently
          // misroutes the error to artwork[0], OR the validation error is
          // dropped entirely when the message identifies the artwork via
          // file_original_index.
          if (currentArtwork?.id != null && (result.artwork?.metadata?.errors?.length ?? 0) > 0) {
            store.setGangsheetValidationError({
              index: findIndexById(store.artworks, currentArtwork.id),
              artwork: result.artwork,
              type: ErrorTypes.INVALID_GANG_SHEET
            });
          }
        }

        if (data.action === "extract_bg_metadata") {
          if (!result.success || !currentArtwork) return;

          const processData = result.process_data;
          if (processData) {
            await markEnhancementCompleted(store, currentArtwork, processData, result, data);
          }

          return;
        }

        if (data.action === "spangle_algorithm_list") {
          if (result.success) {
            return;
          }
        }

        if (data.action === "fetch_price") {
          if (result.success) {
            let { response } = result as {
              response: Record<string, unknown> & { artworks: Record<string, unknown> };
            };

            setPDPDatesLoading(true);

            // PO-3556: while a Spangle is still generating variations, the
            // backend emits interim fetch_price broadcasts that reflect the
            // current artwork dimensions — which can disagree with the final
            // spangled size. Skip the pricing write so the PDP doesn't render
            // mismatched values (e.g. W/H 6x6 next to a Total priced at 10x10).
            // The delivery-date loading signal above still fires, so progress
            // animations keep ticking; once spangle_image lands (mode
            // transitions to sizeEdited), that handler calls
            // getArtworksPricing() which refreshes with the final dimensions.
            if (store.isSpangle && spangleStore.spanglesLoading) {
              return;
            }

            if (store.showMultiSize) {
              const artworksMap: Record<string, unknown[]> = {};

              Object.keys(response.artworks).forEach(key => {
                artworksMap[key] = [(response.artworks as Record<string, unknown>)[key]];
              });

              response = { ...response, artworks: artworksMap };
            }

            store.setArtworkPricing({
              response,
              isMultiSize: store.showMultiSize
            });

            return;
          }
        }

        if (data.action === "fetch_delivery_date") {
          if (result.success) {
            setPDPDatesLoading(false);
          }

          if (!currentArtwork) return;

          const { imageProcessingCompletedEventSent } = currentArtwork;

          updateHTML(result.result);

          if (store.isSpangle) {
            spangleStore.setOriginalArtworkUrl(result.artwork?.url ?? "");
          } else if (store.isGoldFoil) {
            store.convertImageToHexColor({ artwork: result.artwork });
          } else {
            // Skip setLoadedArtwork when result.artwork has no id — the store
            // resolves the target via findIndexById(artwork.id), which returns
            // 0 on miss and would silently overwrite artwork[0]. Losing a
            // frame update is strictly better than corrupting an innocent artwork.
            if (result.artwork?.id != null) {
              store.setLoadedArtwork({ artwork: result.artwork });
            }

            // findIndexById returns 0 on miss; gate on the locally-resolved
            // currentArtwork (supports file_original_index fallback) and use
            // its id so the color lands on the correct artwork.
            if (currentArtwork?.id != null) {
              const whitePixelsRatio = Number(result.artwork?.metadata?.white_pixels_ratio || 0);
              store.setPresetMockupColor({
                index: findIndexById(store.artworks, currentArtwork.id),
                color: whitePixelsRatio > 50 ? "#000000" : "#E1E1E1"
              });
            }

            if (store.isSticker || store.isStickerSample) {
              store.artworks.forEach((artwork, artworkIndex: number) => {
                if (artwork.id) {
                  const shape = artwork.shape || DEFAULT_STICKER_VALUES.shape;
                  const paddingSize =
                    artwork.paddingSize === "no_padding"
                      ? DEFAULT_STICKER_VALUES.paddingSize
                      : artwork.paddingSize;
                  const pixelRatio = Number(artwork.metadata?.whitePixelsRatio || 0);
                  const bgColor =
                    artwork.bgColor ||
                    (pixelRatio > BLACK_STICKER_BACKGROUND_THRESHOLD
                      ? "#000000"
                      : DEFAULT_STICKER_VALUES.bgColor);

                  store.updateArtworkValuesById({
                    id: artwork.id,
                    shape,
                    bgColor,
                    paddingSize
                  });

                  // Reload artwork after updateArtworkValuesById. noUncheckedIndexedAccess
                  // types this as potentially undefined; skip the stale iteration
                  // instead of asserting non-null.
                  const updatedArtwork = store.artworks[artworkIndex];
                  if (!updatedArtwork) return;

                  stickerStore.generateStickerImage({
                    artwork: updatedArtwork as Parameters<
                      typeof stickerStore.generateStickerImage
                    >[0]["artwork"],
                    shape: shape as string,
                    bgColor: bgColor as string,
                    paddingSize: paddingSize as string
                  });
                }
              });
            }
          }

          if (store.isStandardArtwork) {
            const { showMultiSize } = store;
            // Use currentArtwork directly - it was already found by getArtwork() which matches
            // by originalIndex when id is not yet set (race condition with HTTP response)
            const metadata = data.result.artwork?.metadata;
            let sizesAndQuantities: Record<string, unknown>[] = [];

            if (showMultiSize) {
              // MN-4778: do not overwrite a customer-picked placement when enhancement completes.
              // MN-5668: when the store is still empty (fresh upload, customer hasn't interacted),
              // seed a single isAutoSeed custom-size entry so isArtworkValid() unblocks Add to Cart.
              // handlePresetSelect strips isAutoSeed entries when the customer picks a named preset,
              // and mapSizeAndQuantity strips the key before the cart payload reaches the backend.
              if (currentArtwork.isSizesEdited) {
                sizesAndQuantities = recalculateCustomSizes(
                  currentArtwork.sizesAndQuantities as Array<{
                    preset: string;
                    width: number;
                    height: number;
                    [key: string]: unknown;
                  }>,
                  metadata?.aspect_ratio as number
                );
              } else {
                const existing =
                  (currentArtwork.sizesAndQuantities as Record<string, unknown>[]) || [];
                sizesAndQuantities = existing.length > 0 ? existing : [seedAutoCustomSize(store)];
              }
            }

            store.updateArtworkValuesById({
              id: currentArtwork.id,
              originalImageUrl: data.result.artwork?.original_image_url,
              enhancedWithBgUrl: data.result.artwork?.with_bg_url,
              enhancedNoBgUrl: data.result.artwork?.no_bg_url,
              enhancedDpi: data.result.artwork?.metadata?.dpi,
              enhancedOriginalDpi: data.result.artwork?.original_dpi,
              originalDpi: 300,
              sliderPreviewUrl: data.result.artwork?.no_bg_no_trim_url,
              originalMime: data.result.artwork?.original_mime,
              enhancementCompleted: true,
              sizesAndQuantities
            });

            store.handleWebSocketEnhancementComplete(currentArtwork.id);
            store.clearValidateArtworkStartEventInterval(currentArtwork.id);

            if (store.isGoldFoil) {
              store.generateGoldFoilImages({ artworkId: currentArtwork.id });
            }

            submitDtfEvent("dtf_image_print_perfect_toggle", {
              business_line: store.businessLine,
              artwork_id: currentArtwork.id,
              enhance_image: true,
              selected_by: "jiffy",
              metadata: data.result.artwork?.metadata
            });
          }

          if (store.artworks.length >= 1 && !store.artworks.some(artwork => artwork.isLoading)) {
            store.getArtworksPricing();
          }

          if (!imageProcessingCompletedEventSent) {
            submitDtfEvent("image_processing_completed", {
              payload: {
                process_data: result,
                stream_name: `dtf_channel_${store.dtfChannelUuid}`,
                action_type: data.action,
                worker_enhancement: data.worker_enhancement
              },
              business_line: store.businessLine,
              artwork_id: result.artwork_id
            });

            // Handle both Full Enhancement (*_imgix_url) and Lightweight Pipeline (*_url) field names
            const enhancedImageUrl =
              result.artwork?.no_bg_imgix_url ||
              result.artwork?.with_bg_imgix_url ||
              result.artwork?.no_bg_url ||
              result.artwork?.with_bg_url;
            const originalImageUrl =
              result.artwork?.original_image_imgix_url || result.artwork?.original_image_url;

            trackRudderstackImageProcessingCompleted({
              ...(result.artwork_id != null && { artwork_id: String(result.artwork_id) }),
              dpi: result.artwork?.metadata?.dpi,
              location: result.artwork?.metadata?.uploaded_on,
              file_name: result.artwork?.filename,
              file_type: result.artwork?.filetype,
              file_size: result.artwork?.filesize,
              business_line: store.businessLine,
              artworks_count: store.artworks.length,
              pre_cut_transfers: store.preCut,
              ...(enhancedImageUrl && { enhanced_image_url: enhancedImageUrl }),
              ...(originalImageUrl && { original_image_url: originalImageUrl })
            });

            if (result.artwork_id != null) {
              // acknowledgeArtwork needs the backend-assigned id — skip when unknown
              api.dtf
                .acknowledgeArtwork({ id: result.artwork_id }, undefined, { silent: true })
                .catch(() => {
                  // Expected: 422 while enhancement is still processing (HB-1851)
                });
            }

            // Always mark local completion so replayed messages don't re-emit
            // image_processing_completed. Prefer currentArtwork.id so this still
            // works when identity comes through file_original_index fallback.
            const completionId = result.artwork_id ?? currentArtwork?.id;
            if (completionId != null) {
              store.updateArtworkValuesById({
                id: completionId,
                imageProcessingCompletedEventSent: true
              });
              store.updatePrintReadyInfoState(completionId);
            }
          }

          return;
        }

        if (result?.artwork && currentArtwork) {
          await markEnhancementCompleted(store, currentArtwork, result.artwork, result, data);
        }

        if (data.action === "spangle_image") {
          spangleStore.setLastSpangledDimensions({
            width: result.artwork?.metadata?.width_inch ?? null,
            height: result.artwork?.metadata?.height_inch ?? null
          });
          store.setLoadedArtwork({ artwork: result.artwork });
          spangleStore.setOriginalArtworkUrl(result.artwork?.url ?? "");
          // PO-3556: Spangle enhancement may change artwork width/height from the initial
          // 6x6 default to the backend-enhanced size (e.g. 2x3, 5x7, 10x10). Without
          // refetching here, the quantity-row unit price, Summary totals, and the
          // shipping-threshold progress bar (subscribed to Events.PRICING_UPDATED) all
          // stay pinned to the cached 6x6 pricing primed at upload time.
          store.getArtworksPricing();

          return;
        }

        if (data.action === "tags_ready") {
          const verdict = result?.upsell_verdict;
          if (verdict === "show_upsell" || verdict === "skip" || verdict === "unknown") {
            jotaiStore.set(upsellVerdictAtom, verdict);
          }
          return;
        }

        if (!result.success) {
          const { artwork } = result;

          // Route to the locally-resolved currentArtwork so that errors
          // identified only by file_original_index still reach the user.
          // findIndexById returns 0 on miss, so gating on currentArtwork?.id
          // keeps an unattributable failure from clobbering artwork[0].
          const errorArtworkId = currentArtwork?.id ?? result.artwork_id;
          if (errorArtworkId != null) {
            store.handleProcessError({
              file: {
                originalIndex: findIndexById(store.artworks, errorArtworkId),
                name: artwork?.name,
                type: artwork?.filetype,
                size: artwork?.filesize,
                width: artwork?.metadata?.width,
                height: artwork?.metadata?.height
              },
              errorResponse: result
            });
          }
        }
      } catch (error) {
        // Prevent unhandled promise rejections from async received()
        console.error("[DtfChannel] received error:", error);
        notifyError("DtfChannel received handler error", {
          params: { error: String(error) }
        });
      }
    },
    connected(this: CableSubscription) {
      const {
        artworkIds,
        artworkWidth,
        artworkHeight,
        processArtwork,
        urlPreCut,
        fileContentType,
        artworkPaddingSize,
        artworkBgColor,
        artworkColor,
        artworkShape
      } = store.actionCableOptions;

      if (artworkIds) {
        store.preloadArtworks({
          artworkIds,
          artworkWidth,
          artworkHeight,
          processArtwork,
          urlPreCut,
          fileContentType,
          artworkPaddingSize,
          artworkBgColor,
          artworkColor,
          artworkShape
        });
      }

      if (!store.actionCableConnected) {
        store.cleanupPolling();
        store.setActionCableConnected(true);
        store.setIsRetrying(true);

        if (store.isSpangle) {
          spangleStore.resetCompletedSpangleData();
        }

        store.artworks.forEach(artwork => {
          // MN-5356: Belt-and-suspenders guard — require both non-empty data and
          // non-empty result on the stored payload before replaying. If setLastEvent
          // was called with a pre-guard codebase (or the payload somehow slipped
          // through), undefined/empty data would spread undefined into the replay,
          // stripping additional_metadata.store from the worker payload. Requiring
          // result ensures last_process_data is also meaningful (it maps to
          // wsData["result"] in the perform call below).
          const wsData = artwork.lastWebSocketEventData as Record<string, unknown> | undefined;
          const wsDataData = wsData?.["data"];
          const wsDataResult = wsData?.["result"];
          const hasReplayData =
            wsDataData != null &&
            typeof wsDataData === "object" &&
            Object.keys(wsDataData).length > 0 &&
            wsDataResult != null &&
            typeof wsDataResult === "object" &&
            Object.keys(wsDataResult as object).length > 0;

          if (
            artwork.lastWebSocketEvent &&
            wsData &&
            Object.keys(wsData).length > 0 &&
            hasReplayData
          ) {
            // Reset artwork processTimeStamp
            // There will be cases where discontinued continue sending messages.
            // Rejecting it to prioritize messages coming from this process since
            // this is the source of truth.
            const processTimeStamp = Date.now();

            this.perform("continue_dtf_artwork_process", {
              last_process: artwork.lastWebSocketEvent,
              last_process_data: wsData["result"] as Record<string, unknown>,
              data: {
                ...(wsData["data"] as Record<string, unknown>),
                processTimeStamp,
                experiments: store.experiments
              }
            });
            store.updateArtworkValuesById({
              id: artwork.id,
              lastWebSocketEvent: null,
              lastWebSocketEventData: {},
              processTimeStamp
            });
          } else if (
            !artwork.enhancementCompleted &&
            (!artwork.lastWebSocketEvent || !hasReplayData)
          ) {
            // Artwork has no WS resume path (or resume data was invalid/empty) —
            // re-schedule polling so it doesn't get stuck.
            store.schedulePollingFallback(artwork.id);
          }
        });
      }
    },
    disconnected() {
      store.setActionCableConnected(false);

      store.artworks.forEach(artwork => {
        if (artwork.isLoading && !artwork.enhancementCompleted) {
          store.schedulePollingFallback(artwork.id, {
            initialDelay: WS_DISCONNECT_POLLING_DELAY
          });
        }
      });
    },
    spangle(this: CableSubscription, data = {}) {
      this.perform("spangle", data);
    }
  });

  return cable;
};
/* eslint-enable max-lines, max-lines-per-function */
